<script lang="ts" setup>
import { JobApplicantStatus } from '@factoryfixinc/ats-interfaces';
import { h, onMounted } from 'vue';
import { computed, ref, watch } from 'vue';

import CheckIcon from '@/assets/svg/check-16.svg?component';
import ChevronIcon from '@/assets/svg/conversations/ChevronIcon.svg?component';
import RejectedModal from '@/components/Conversations/Application/RejectedModal.vue';
import {
  REJECTED_WITH_REASON,
  jobApplicationOptions,
} from '@/core/conversations/pro-profile/utils/application-statuses';

const props = withDefaults(
  defineProps<{
    status?: JobApplicantStatus;
    rejectionReason?: string;
    slim?: boolean;
  }>(),
  {
    showDownloadProfile: false,
    slim: false,
  },
);

const emit = defineEmits(['updateValue']);

// The svg loader strips the viewBox, so at the 18px menu-icon size the 24-unit path is cropped.
const MenuChevron = () => h(ChevronIcon, { viewBox: '0 0 24 24' });

const selectedApplicationStatus = ref();
const applicationStatuses = computed(() => {
  return jobApplicationOptions.filter((option) => option.value !== JobApplicantStatus.CONTACT);
});
let previousStatus: string;

const updateValue = (newStatus: string) => {
  previousStatus = selectedApplicationStatus.value;
  selectedApplicationStatus.value = newStatus as JobApplicantStatus;

  if (newStatus === REJECTED_WITH_REASON) {
    rejectedModalVisible.value = true;
    return;
  }
  rejectedModalVisible.value = false;
  if (newStatus === previousStatus) return;
  emit('updateValue', { newStatus: newStatus as JobApplicantStatus, previousStatus });
};

const updateRejectedReason = (reason: string, isSilent: boolean) => {
  rejectedModalVisible.value = false;
  emit('updateValue', { newStatus: JobApplicantStatus.REJECTED, previousStatus, reason, isSilent });
};

onMounted(() => {
  if (
    props.rejectionReason &&
    props.rejectionReason.length > 0 &&
    props.status === JobApplicantStatus.REJECTED
  ) {
    selectedApplicationStatus.value = REJECTED_WITH_REASON;
  } else if (props.status === JobApplicantStatus.CONTACT) {
    selectedApplicationStatus.value = 'Sourcing';
  } else {
    selectedApplicationStatus.value = props.status;
  }
  previousStatus = selectedApplicationStatus.value;
});

const computedStatus = computed(() => props.status);
watch(computedStatus, (newValue) => {
  selectedApplicationStatus.value = newValue;
});

const rejectedModalVisible = ref(false);

// If user decides to close the modal without submitting, then we set the
// preivous status back to the selected status
const setPreviousStatus = () => {
  selectedApplicationStatus.value = props.status;
};
</script>
<template>
  <div
    data-test="application-status-picker"
    class="application-picker flex items-center justify-end"
    :class="slim ? 'slim min-w-min' : 'min-w-[175px]'"
  >
    <v-select
      :items="applicationStatuses"
      density="compact"
      variant="solo"
      :modelValue="selectedApplicationStatus"
      :menuIcon="MenuChevron"
      :menu-props="{
        maxHeight: 'unset',
        offset: '5px',
        location: 'bottom end',
        contentClass: '!min-w-[180px] menu-card--sm',
      }"
      @update:modelValue="updateValue"
      :persistent-counter="true"
      data-test="application-status-picker-select"
    >
      <template v-slot:item="{ props }">
        <v-list-item
          :ripple="false"
          v-bind="props"
          :data-test="`application-status-option-${String(props.value)
            .toLowerCase()
            .replace(/\s+/g, '-')}`"
        >
          <template #append="{ isSelected }">
            <CheckIcon
              v-if="isSelected"
              class="shrink-0 text-[var(--menu-row-selected-text)]"
              width="16"
              height="16"
              aria-hidden="true"
            />
          </template>
        </v-list-item>
      </template>
    </v-select>

    <RejectedModal
      v-model="rejectedModalVisible"
      @on-cancel="setPreviousStatus"
      @on-update-rejected-reason="updateRejectedReason"
    ></RejectedModal>
  </div>
</template>
<style lang="postcss">
.application-picker {
  &.slim {
    @apply h-7;
  }
  .v-input {
    @apply h-8 max-w-max;
  }
  &.slim .v-input {
    @apply h-7;
  }
  .v-input__details {
    @apply hidden;
  }

  .v-field {
    @apply rounded-lg border border-tint-80 font-sans text-xs font-semibold;
  }

  .v-field,
  .v-field__field,
  .v-field__input {
    @apply flex h-8 max-h-8 items-center;
  }

  &.slim .v-field,
  &.slim .v-field__field,
  &.slim .v-field__input {
    @apply h-7 max-h-7;
  }

  &.slim .v-field {
    @apply rounded-md;
  }

  .v-field__input {
    @apply justify-center p-0 shadow-none;
    .v-select__selection {
      @apply text-shade-900;
    }
  }

  .v-field--appended {
    @apply pl-2 pr-1;
  }

  .v-field--variant-solo,
  .v-field--variant-solo-filled {
    @apply shadow-none;
  }
}
</style>
